import { MessageSquarePlus } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { Button } from '@/components/ui';
import { cn } from '@/lib/cn';
import type { ThreadSummary } from '@/types/api';

import { useThreads } from '../hooks';
import { useChatStore } from '../store/chatStore';

export function ThreadList() {
  const { data, isLoading } = useThreads();
  const { threadId: activeThreadId } = useParams();
  const newChat = useChatStore((s) => s.newChat);

  return (
    <aside className="flex h-full min-h-0 w-full flex-col border-r border-slate-200 bg-white">
      <div className="shrink-0 border-b border-slate-200 px-3 py-3">
        <Button asChild variant="secondary" size="sm" className="w-full justify-start" onClick={newChat}>
          <Link to="/app/chat">
            <MessageSquarePlus className="mr-2 h-4 w-4" aria-hidden />
            New chat
          </Link>
        </Button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-2 py-3 text-body">
        {isLoading && (
          <div className="px-2 text-small text-slate-500">Loading threads…</div>
        )}
        {!isLoading && (data?.length ?? 0) === 0 && (
          <div className="px-2 text-small text-slate-500">No conversations yet.</div>
        )}
        <ul className="space-y-1">
          {(data ?? []).map((thread) => (
            <ThreadRow
              key={thread.thread_id}
              thread={thread}
              active={activeThreadId === thread.thread_id}
            />
          ))}
        </ul>
      </div>
    </aside>
  );
}

function ThreadRow({ thread, active }: { thread: ThreadSummary; active: boolean }) {
  const date = new Date(thread.last_activity);
  // Sidebar title = first sentence of the first user question, falls
  // back to a short ID stamp when the thread has no user turn yet.
  const title =
    firstSentence(thread.first_question, 80) ?? `Thread ${thread.thread_id.slice(0, 8)}`;
  return (
    <li>
      <Link
        to={`/app/chat/${thread.thread_id}`}
        className={cn(
          'block rounded px-2 py-1.5 text-body',
          active ? 'bg-brand-50 text-brand-900' : 'text-slate-700 hover:bg-slate-100',
        )}
      >
        <div className="flex items-baseline justify-between gap-1">
          <span
            className="truncate font-medium"
            title={thread.first_question ?? thread.thread_id}
          >
            {title}
          </span>
          <span className="shrink-0 text-small text-slate-500">{date.toLocaleDateString()}</span>
        </div>
        <div className="text-small text-slate-500">{thread.turn_count} turns</div>
      </Link>
    </li>
  );
}

/** Extract the first sentence of a question for sidebar display. */
function firstSentence(text: string | null | undefined, maxLen: number): string | null {
  if (!text) return null;
  const cleaned = text.trim().replace(/\s+/g, ' ');
  if (!cleaned) return null;
  // Match up to and including the first terminal punctuation; otherwise
  // take the line as-is.
  const m = cleaned.match(/^([^.!?\n]+[.!?])(?:\s|$)/);
  const sentence = m ? m[1] : cleaned;
  return sentence.length > maxLen ? `${sentence.slice(0, maxLen - 1)}…` : sentence;
}
